import { z } from 'zod';

// Tracker entries as stored by the server. Units are metric internally: ml, g, mm, °C.
// endAt: for duration types (sleep, breast, pump, bottle) null means "ongoing";
// for point-in-time types (diaper, medicine, growth, temperature, solids, custom, note) it is null/ignored.

export const ENTRY_TYPES = [
  'sleep',
  'breast',
  'bottle',
  'pump',
  'solids',
  'diaper',
  'medicine',
  'growth',
  'temperature',
  'custom',
  'note',
] as const;
export type EntryType = (typeof ENTRY_TYPES)[number];

/** Types whose endAt = null means "still running". */
export const DURATION_ENTRY_TYPES: readonly EntryType[] = ['sleep', 'breast', 'pump', 'bottle'];

export const isoDateTimeSchema = z.iso.datetime({ offset: true });

export const breastSideSchema = z.enum(['left', 'right']);
export type BreastSide = z.infer<typeof breastSideSchema>;

/** Set when the app closed a forgotten timer itself with a guessed end; cleared once a parent confirms or fixes it. */
export const autoEndedSchema = z.object({ at: isoDateTimeSchema }).optional();

export const sleepDataSchema = z.object({
  autoEnded: autoEndedSchema,
  /** crib = own crib/bassinet; own_bed = toddler's own bed; parents_bed = shared adult bed (safe-sleep relevant). */
  location: z.enum(['crib', 'own_bed', 'parents_bed', 'stroller', 'car', 'arms', 'other']).optional(),
  fellAsleep: z.enum(['independent', 'nursing', 'rocking', 'other']).optional(),
});

export const breastSegmentSchema = z.object({
  side: breastSideSchema,
  startAt: isoDateTimeSchema,
  endAt: isoDateTimeSchema.nullable(),
});

export const breastDataSchema = z.object({
  segments: z.array(breastSegmentSchema),
  autoEnded: autoEndedSchema,
  lastSide: breastSideSchema.optional(),
});

export const bottleDataSchema = z.object({
  amountMl: z.number().nonnegative().max(1000),
  content: z.enum(['breastmilk', 'formula', 'mixed', 'other']),
  autoEnded: autoEndedSchema,
});

export const pumpDataSchema = z.object({
  amountMlLeft: z.number().nonnegative().max(1000).optional(),
  amountMlRight: z.number().nonnegative().max(1000).optional(),
  amountMlTotal: z.number().nonnegative().max(2000).optional(),
  autoEnded: autoEndedSchema,
});

export const solidsDataSchema = z.object({
  foods: z.array(z.string().min(1).max(100)).max(50).optional(),
  reaction: z.string().max(500).optional(),
});

export const diaperKindSchema = z.enum(['wet', 'dirty', 'mixed', 'dry']);
export type DiaperKind = z.infer<typeof diaperKindSchema>;

export const diaperDataSchema = z.object({ kind: diaperKindSchema });

export const medicineDataSchema = z.object({
  name: z.string().min(1).max(200),
  dose: z.number().nonnegative().optional(),
  unit: z.string().max(20).optional(),
});

export const growthDataSchema = z.object({
  weightG: z.number().positive().max(40_000).optional(),
  lengthMm: z.number().positive().max(1_500).optional(),
  headMm: z.number().positive().max(800).optional(),
});

export const temperatureDataSchema = z.object({
  celsius: z.number().min(25).max(45),
  method: z.enum(['rectal', 'axillary', 'oral', 'ear', 'forehead', 'other']).optional(),
});

export const customDataSchema = z.object({
  value: z.union([z.string().max(500), z.number()]).optional(),
  autoEnded: autoEndedSchema,
});

export const noteDataSchema = z.object({});

const baseShape = {
  id: z.uuid(),
  babyId: z.uuid(),
  startAt: isoDateTimeSchema,
  endAt: isoDateTimeSchema.nullable(),
  note: z.string().max(2000).nullish(),
  categoryId: z.string().max(100).nullish(),
};

export const sleepEntrySchema = z.object({ ...baseShape, type: z.literal('sleep'), data: sleepDataSchema });
export const breastEntrySchema = z.object({ ...baseShape, type: z.literal('breast'), data: breastDataSchema });
export const bottleEntrySchema = z.object({ ...baseShape, type: z.literal('bottle'), data: bottleDataSchema });
export const pumpEntrySchema = z.object({ ...baseShape, type: z.literal('pump'), data: pumpDataSchema });
export const solidsEntrySchema = z.object({ ...baseShape, type: z.literal('solids'), data: solidsDataSchema });
export const diaperEntrySchema = z.object({ ...baseShape, type: z.literal('diaper'), data: diaperDataSchema });
export const medicineEntrySchema = z.object({ ...baseShape, type: z.literal('medicine'), data: medicineDataSchema });
export const growthEntrySchema = z.object({ ...baseShape, type: z.literal('growth'), data: growthDataSchema });
export const temperatureEntrySchema = z.object({
  ...baseShape,
  type: z.literal('temperature'),
  data: temperatureDataSchema,
});
export const customEntrySchema = z.object({ ...baseShape, type: z.literal('custom'), data: customDataSchema });
export const noteEntrySchema = z.object({ ...baseShape, type: z.literal('note'), data: noteDataSchema });

export const entrySchema = z
  .discriminatedUnion('type', [
    sleepEntrySchema,
    breastEntrySchema,
    bottleEntrySchema,
    pumpEntrySchema,
    solidsEntrySchema,
    diaperEntrySchema,
    medicineEntrySchema,
    growthEntrySchema,
    temperatureEntrySchema,
    customEntrySchema,
    noteEntrySchema,
  ])
  .superRefine((e, ctx) => {
    if (e.endAt !== null && Date.parse(e.endAt) < Date.parse(e.startAt)) {
      ctx.addIssue({ code: 'custom', path: ['endAt'], message: 'endAt must not be before startAt' });
    }
    if (e.type === 'growth' && e.data.weightG === undefined && e.data.lengthMm === undefined && e.data.headMm === undefined) {
      ctx.addIssue({ code: 'custom', path: ['data'], message: 'growth entry needs at least one measurement' });
    }
  });

export type Entry = z.infer<typeof entrySchema>;
export type SleepEntry = z.infer<typeof sleepEntrySchema>;
export type BreastEntry = z.infer<typeof breastEntrySchema>;
export type BottleEntry = z.infer<typeof bottleEntrySchema>;
export type PumpEntry = z.infer<typeof pumpEntrySchema>;
export type DiaperEntry = z.infer<typeof diaperEntrySchema>;
export type EntryOfType<T extends EntryType> = Extract<Entry, { type: T }>;

export type SleepData = z.infer<typeof sleepDataSchema>;
export type BreastData = z.infer<typeof breastDataSchema>;
export type BottleData = z.infer<typeof bottleDataSchema>;
export type PumpData = z.infer<typeof pumpDataSchema>;
export type SolidsData = z.infer<typeof solidsDataSchema>;
export type DiaperData = z.infer<typeof diaperDataSchema>;
export type MedicineData = z.infer<typeof medicineDataSchema>;
export type GrowthData = z.infer<typeof growthDataSchema>;
export type TemperatureData = z.infer<typeof temperatureDataSchema>;
export type CustomData = z.infer<typeof customDataSchema>;

export function isEntryOfType<T extends EntryType>(e: Entry, type: T): e is EntryOfType<T> {
  return e.type === type;
}

/** Validate unknown input; throws ZodError on failure. */
export function parseEntry(input: unknown): Entry {
  return entrySchema.parse(input);
}

/** Total pumped volume: explicit total wins, else left + right. */
export function pumpTotalMl(d: PumpData): number {
  if (d.amountMlTotal !== undefined) return d.amountMlTotal;
  return (d.amountMlLeft ?? 0) + (d.amountMlRight ?? 0);
}
